import React from 'react';
import { APITypes, MedicalProfileFiles } from '../../../../../primary-record-types';
import Alert from '../../../../v2BaseComponents/Alert';
import { getIntl } from '../../../../../utils/intl';
import { supportEmail } from '../../../../../utils/constants';
import LargeAlertPaper from '../../../../v2BaseComponents/LargeAlertPaper';
import Stack from '../../../../v2BaseComponents/Stack';
import MainContentContainer, { mainContentPadding } from '../../../../v2BaseComponents/MainContentContainer';
import FileViewer from './FileViewer';
import AudioPlayer from './AudioPlayer';

const intl = getIntl('profile-file-contents', {
    fileProcessingFailed: 'File processing failed',
    encryptedPDFHeader: 'PDF is encrypted',
    fileProcessingFailedMessage: `File processing failed. Please try again later.  If the problem persists, please contact support ${supportEmail}.`,
    encryptedPDFFailedMessage: `This file is encrypted and cannot be processed. Please remove the encryption and reupload the file.`,
    unsupportedFileTypeHeader: 'File type not supported',
    unsupportedFileTypeMessage: `This file type can't be processed, so there is nothing to view or search. You can still download it. Converting it to a PDF and reuploading will make it searchable.`,
    fileTooLargeHeader: 'File is too large',
    fileTooLargeMessage: `This text file is too large to convert for viewing and search. You can still download it. Splitting it into smaller files and reuploading will make it searchable.`,
    reprocess: 'Reprocess',
    processingHeader: 'File is processing',
    processingContent: 'Please wait while the file is being processed. This may take a few minutes.',
});

export type BoundingBoxParams = {
    bbTop?: string;
    bbLeft?: string;
    bbWidth?: string;
    bbHeight?: string;
};

export type FileContentsOwnProps = {
    file: APITypes.medicalProfileFilesService.MedicalProfileFileListItem;
    onReprocess: () => void;
    isReprocessing: boolean;
    hasFilesEdit: boolean;
    page: string | undefined;
    boundingBoxParams: BoundingBoxParams;
    startTime: string | undefined;
};

export type FileContentsProps = FileContentsOwnProps;

const getNumericParamValue = (param: string | undefined, isInt: boolean): number | undefined => {
    if (param !== undefined) {
        const parsed = isInt ? parseInt(param, 10) : parseFloat(param);
        if (!isNaN(parsed)) {
            return parsed;
        }
    }
    return undefined;
};

const maybeGetBoundingBox = (boundingBoxParams: BoundingBoxParams): MedicalProfileFiles.BoundingBox | undefined => {
    const { bbTop, bbLeft, bbWidth, bbHeight } = boundingBoxParams;
    const bbTopNum = getNumericParamValue(bbTop, false);
    const bbLeftNum = getNumericParamValue(bbLeft, false);
    const bbWidthNum = getNumericParamValue(bbWidth, false);
    const bbHeightNum = getNumericParamValue(bbHeight, false);

    if (bbTopNum !== undefined && bbLeftNum !== undefined && bbWidthNum !== undefined && bbHeightNum !== undefined) {
        return {
            top: bbTopNum,
            left: bbLeftNum,
            width: bbWidthNum,
            height: bbHeightNum,
        };
    }
    return undefined;
};

const FileContents: React.FC<FileContentsProps> = ({
    file,
    onReprocess,
    isReprocessing,
    hasFilesEdit,
    page,
    boundingBoxParams,
    startTime,
}) => {
    const highlightBoundingBox = maybeGetBoundingBox(boundingBoxParams);

    const renderViewerOrPlayer = () => {
        if (!file) return null;

        if (file.processingType === 'image' || (file.processingType === 'pdf' && file.pageCount !== null)) {
            return (
                <MainContentContainer>
                    <Stack vertical fullWidth style={{ paddingBottom: mainContentPadding }}>
                        <FileViewer
                            key={file.medicalProfileFileId}
                            defaultPage={getNumericParamValue(page, true)}
                            file={file}
                            pageCount={file.pageCount!}
                            highlightBoundingBox={highlightBoundingBox}
                        />
                    </Stack>
                </MainContentContainer>
            );
        } else if (file.processingType === 'audio') {
            return (
                <AudioPlayer
                    key={file.medicalProfileFileId}
                    file={file}
                    defaultStartTime={getNumericParamValue(startTime, true)}
                />
            );
        } else {
            return null;
        }
    };

    if (file.processingStatus === 'processing') {
        return (
            <MainContentContainer>
                <LargeAlertPaper
                    icon="RefreshCw"
                    title={intl('processingHeader')}
                    message={intl('processingContent')}
                />
            </MainContentContainer>
        );
    } else if (file.processingStatus === 'failed') {
        let failureTitle = intl('fileProcessingFailed');
        let failureMessage = intl('fileProcessingFailedMessage');
        if (file.failureType === 'encrypted-pdf') {
            failureTitle = intl('encryptedPDFHeader');
            failureMessage = intl('encryptedPDFFailedMessage');
        } else if (file.failureType === 'unsupported-content-type') {
            failureTitle = intl('unsupportedFileTypeHeader');
            failureMessage = intl('unsupportedFileTypeMessage');
        } else if (file.failureType === 'file-too-large') {
            failureTitle = intl('fileTooLargeHeader');
            failureMessage = intl('fileTooLargeMessage');
        }

        // Reprocessing cannot change the outcome for these, so offering it would guarantee a second failure.
        const canReprocess =
            hasFilesEdit &&
            !(file.failureType && MedicalProfileFiles.NON_REPROCESSABLE_FAILURE_TYPES.includes(file.failureType));

        return (
            <MainContentContainer>
                <Alert
                    type="error"
                    title={failureTitle}
                    message={failureMessage}
                    actionItems={
                        canReprocess
                            ? [
                                  {
                                      title: intl('reprocess'),
                                      onPress: onReprocess,
                                      isDisabled: isReprocessing,
                                  },
                              ]
                            : undefined
                    }
                />
            </MainContentContainer>
        );
    } else {
        return renderViewerOrPlayer();
    }
};

export default React.memo(FileContents);
